import React, { useEffect, useState } from 'react';
import { X, BarChart3, Users, CheckCircle2, Clock, MessageSquare, TrendingUp, RefreshCw } from 'lucide-react';
import { useTheme } from './ThemeContext';

export const AnalyticsDrawer = ({ flowId, tenantId, onClose }) => {
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/app/${tenantId}/api/chatbot-flows/${flowId}/analytics`);
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch (e) {
      console.error('Failed to load analytics', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (flowId && tenantId) {
      fetchAnalytics();
    }
  }, [flowId, tenantId]);

  return (
    <div className={`absolute right-0 top-0 bottom-0 z-30 w-96 border-l backdrop-blur-md p-5 flex flex-col h-full overflow-y-auto text-xs transition-colors duration-200 shadow-2xl ${
      isDark ? 'border-slate-800 bg-slate-900/95 text-slate-200' : 'border-slate-200 bg-white/95 text-slate-800 shadow-2xl'
    }`}>
      {/* Header */}
      <div className={`flex items-center justify-between pb-4 border-b ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
            <BarChart3 className="h-4 w-4" />
          </div>
          <div>
            <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Flow Analytics</h3>
            <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Live WhatsApp Drop-off & Stats</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={fetchAnalytics}
            title="Refresh statistics"
            className={`p-1.5 rounded-lg transition-colors ${
              isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-500 hover:bg-slate-100'
            }`}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin text-blue-500' : ''}`} />
          </button>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-lg transition-colors ${
              isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-500 hover:bg-slate-100'
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-400 py-16">
          <RefreshCw className="h-5 w-5 animate-spin text-blue-500" />
          <span>Loading analytics...</span>
        </div>
      ) : data ? (
        <div className="py-4 space-y-4">
          {/* Top Metric Cards */}
          <div className="grid grid-cols-2 gap-2.5">
            <div className={`p-3 rounded-xl border ${
              isDark ? 'bg-slate-800/50 border-slate-700/60' : 'bg-slate-50 border-slate-200'
            }`}>
              <div className="flex items-center gap-1.5 text-slate-400 text-[10px] uppercase font-bold tracking-wider">
                <TrendingUp className="h-3 w-3 text-purple-500" /> Total Triggers
              </div>
              <div className="text-xl font-black mt-1 text-purple-500">{data.total_executions || 0}</div>
            </div>

            <div className={`p-3 rounded-xl border ${
              isDark ? 'bg-slate-800/50 border-slate-700/60' : 'bg-slate-50 border-slate-200'
            }`}>
              <div className="flex items-center gap-1.5 text-slate-400 text-[10px] uppercase font-bold tracking-wider">
                <CheckCircle2 className="h-3 w-3 text-emerald-500" /> Completed
              </div>
              <div className="text-xl font-black mt-1 text-emerald-500">{data.completed_sessions || 0}</div>
            </div>

            <div className={`p-3 rounded-xl border ${
              isDark ? 'bg-slate-800/50 border-slate-700/60' : 'bg-slate-50 border-slate-200'
            }`}>
              <div className="flex items-center gap-1.5 text-slate-400 text-[10px] uppercase font-bold tracking-wider">
                <Clock className="h-3 w-3 text-amber-500" /> Active Chats
              </div>
              <div className="text-xl font-black mt-1 text-amber-500">{data.active_sessions || 0}</div>
            </div>

            <div className={`p-3 rounded-xl border ${
              isDark ? 'bg-slate-800/50 border-slate-700/60' : 'bg-slate-50 border-slate-200'
            }`}>
              <div className="flex items-center gap-1.5 text-slate-400 text-[10px] uppercase font-bold tracking-wider">
                <Users className="h-3 w-3 text-blue-500" /> Total Users
              </div>
              <div className="text-xl font-black mt-1 text-blue-500">{data.total_sessions || 0}</div>
            </div>
          </div>

          {/* Button Clicks Ranking */}
          {data.button_clicks && Object.keys(data.button_clicks).length > 0 && (
            <div className={`p-3.5 rounded-xl border ${
              isDark ? 'bg-slate-800/40 border-slate-800' : 'bg-slate-50 border-slate-200'
            }`}>
              <h4 className={`text-[11px] font-bold uppercase tracking-wider mb-2.5 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                Button Selection Popularity
              </h4>
              <div className="space-y-2">
                {Object.entries(data.button_clicks).map(([btnName, count]) => {
                  const maxClicks = Math.max(...Object.values(data.button_clicks)) || 1;
                  const pct = Math.min(100, Math.round((count / maxClicks) * 100));
                  return (
                    <div key={btnName} className="space-y-1">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="font-medium truncate max-w-[200px]">{btnName}</span>
                        <span className="font-bold text-emerald-500">{count} clicks</span>
                      </div>
                      <div className={`w-full h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                        <div className="h-1.5 rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent Interaction Audit Logs */}
          <div className={`p-3.5 rounded-xl border ${
            isDark ? 'bg-slate-800/40 border-slate-800' : 'bg-slate-50 border-slate-200'
          }`}>
            <h4 className={`text-[11px] font-bold uppercase tracking-wider mb-2.5 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              Recent Activity Logs
            </h4>
            {(!data.recent_logs || data.recent_logs.length === 0) ? (
              <div className="text-center py-4 text-slate-400 text-xs">No execution logs yet. Test flow to see logs!</div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {data.recent_logs.map((log) => (
                  <div key={log.id} className={`p-2 rounded-lg border text-[11px] ${
                    isDark ? 'border-slate-800 bg-slate-900/60' : 'border-slate-200 bg-white'
                  }`}>
                    <div className="flex items-center justify-between font-mono text-[10px] text-slate-400">
                      <span>{log.phone}</span>
                      <span>{log.created_at}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                        log.direction === 'inbound'
                          ? 'bg-blue-500/20 text-blue-400'
                          : log.direction === 'outbound'
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {log.direction}
                      </span>
                      <span className="font-semibold text-slate-700 dark:text-slate-200">{log.node_type}</span>
                    </div>
                    <div className="text-slate-500 dark:text-slate-400 mt-1 truncate" title={log.payload_preview}>
                      {log.payload_preview}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};
